import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { nameFrom } from "@/lib/dealPitch";

const BASE = { admin: "/hub", lender: "/lender", referrer: "/refer" };
const path = (role, id) => `${BASE[role] || "/lender"}/leads/${id}/company-check`;

/** The company check on one case — re-reads itself while it is still being pulled. */
export function useCompanyCheck(leadId, role) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get(path(role, leadId));
      setData(r.data);
    } catch {
      setData({ status: "none" });
    }
  }, [leadId, role]);

  useEffect(() => { polls.current = 0; load(); }, [load]);

  useEffect(() => {
    const busy = ["running", "queued"].includes(data?.status) || data?.docs?.reading > 0;
    if (!busy || polls.current > 30) return undefined;
    const t = setTimeout(() => { polls.current += 1; load(); }, 8000);
    return () => clearTimeout(t);
  }, [data, load]);

  const run = async (companyNumber) => {
    setBusy(true);
    try {
      const r = await api.post(path("admin", leadId), companyNumber ? { company_number: companyNumber } : {});
      polls.current = 0;
      setData((d) => ({ ...d, ...r.data }));
      return r.data;
    } finally { setBusy(false); }
  };

  const pdf = async () => {
    const r = await api.get(`${BASE[role] || "/lender"}/leads/${leadId}/company-check.pdf`, { responseType: "blob" });
    const c = data?.report?.company || {};
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = nameFrom(r, `Connexis Company Check - ${c.name || "Company"}${c.number ? ` (${c.number})` : ""}.pdf`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return { data, busy, run, pdf, reload: load };
}

export const money = (v) => {
  if (v === null || v === undefined) return "—";
  const a = Math.abs(v);
  const s = a >= 1e9 ? `£${(a / 1e9).toFixed(2)}bn` : a >= 1e6 ? `£${(a / 1e6).toFixed(1)}m` : a >= 1e4 ? `£${Math.round(a / 1e3)}k` : `£${Math.round(a).toLocaleString("en-GB")}`;
  return v < 0 ? `-${s}` : s;
};

export const day = (s) => {
  if (!s) return "—";
  const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export const born = (b) => {
  if (!b?.year) return null;
  const m = b.month ? new Date(2000, Number(b.month) - 1, 1).toLocaleDateString("en-GB", { month: "short" }) : "";
  return `${m} ${b.year}`.trim();
};

export const creditTone = (c) => {
  const s = (c?.status || "").toLowerCase();
  if (/risk|insolv|fail|dissol/.test(s)) return "red";
  if (/caution|moderate|watch/.test(s)) return "amber";
  if (/secure|stable|low/.test(s)) return "green";
  if (c?.score == null) return "slate";
  return c.score < 30 ? "red" : c.score < 50 ? "amber" : "green";
};
